import { Link } from 'react-router-dom';
import {
  IoNotificationsOutline,
  IoWalletOutline,
  IoSparklesOutline,
  IoPersonCircle,
  IoLogOutOutline,
  IoSettingsOutline,
} from 'react-icons/io5';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { cn } from '@/components/ui/utils';
import { RecipientSelector } from './RecipientSelector';
import { useAuth } from '../../context/AuthContext';

/**
 * Top bar (PEL-817) — always visible at top of the authenticated Shell.
 *
 * Desktop / tablet layout:
 *   [RecipientSelector ........... Wallet chip | Points chip | Bell | Avatar]
 *
 * Mobile layout (<768px):
 *   [RecipientSelector ............................... Avatar]
 *   Wallet + Points + Bell collapse into the avatar menu to keep the bar
 *   narrow enough on phones; PeliPoints/Wallet each have their own tab so
 *   they're one tap away regardless.
 *
 * Data: All chips render stub / placeholder values. PEL-818 wires real
 * AuthContext + balance queries.
 */

function WalletChip({ className }: { className?: string }) {
  return (
    <Link
      to="/app/wallet"
      className={cn(
        'inline-flex items-center gap-1.5',
        'h-9 px-3 rounded-full',
        'bg-[var(--cta-light)] text-[var(--cta)]',
        'text-[13px] font-semibold',
        'hover:opacity-90 transition-opacity',
        className,
      )}
      aria-label="Wallet balance"
    >
      <IoWalletOutline size={16} aria-hidden />
      <span>$0.00</span>
    </Link>
  );
}

function PointsChip({ className }: { className?: string }) {
  return (
    <Link
      to="/app/pelipoints"
      className={cn(
        'inline-flex items-center gap-1.5',
        'h-9 px-3 rounded-full',
        'bg-[var(--accent-aqua-light)] text-[var(--accent-aqua)]',
        'text-[13px] font-semibold',
        'hover:opacity-90 transition-opacity',
        className,
      )}
      aria-label="PeliPoints balance"
    >
      <IoSparklesOutline size={16} aria-hidden />
      <span>0 pts</span>
    </Link>
  );
}

function NotificationsBell({ className }: { className?: string }) {
  return (
    <Button
      variant="ghost"
      size="icon"
      className={cn('relative', className)}
      aria-label="Notifications"
    >
      <IoNotificationsOutline size={22} />
      {/* Unread indicator dot — stub hidden */}
      <span
        className="absolute top-2 right-2 w-2 h-2 rounded-full bg-[var(--cta)] hidden"
        aria-hidden
      />
    </Button>
  );
}

function AvatarMenu() {
  const { user, logout } = useAuth();

  const displayName = user
    ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || 'Account'
    : 'Guest';
  const displayEmail = user?.email ?? '';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center justify-center',
            'w-10 h-10 rounded-full',
            'bg-[var(--brand-light)] text-[var(--brand)]',
            'hover:opacity-90 transition-opacity',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2',
          )}
          aria-label="Account menu"
        >
          <IoPersonCircle size={28} aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[220px]">
        <DropdownMenuLabel>
          <div className="flex flex-col leading-tight">
            <span className="text-[14px] font-semibold">{displayName}</span>
            {displayEmail && (
              <span className="text-[12px] text-[var(--text-muted)]">
                {displayEmail}
              </span>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/app/profile" className="flex items-center gap-2">
            <IoSettingsOutline size={16} aria-hidden />
            <span>Profile &amp; settings</span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => {
            void logout();
          }}
        >
          <IoLogOutOutline size={16} aria-hidden />
          <span>Sign out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function TopBar() {
  return (
    <header
      className={cn(
        'sticky top-0 z-30',
        'h-16 shrink-0',
        'bg-[var(--surface)] border-b border-[var(--border)]',
        'flex items-center gap-3',
        'px-4 md:px-6',
      )}
    >
      {/* Mobile-only brand mark to anchor the left edge when sidebar is hidden */}
      <Link
        to="/app"
        className="md:hidden flex items-center"
        aria-label="Pelipost home"
      >
        <img
          src="/logo-horizontal.png"
          alt="Pelipost"
          className="h-6 w-auto"
        />
      </Link>

      {/* Recipient selector takes the remaining room */}
      <div className="flex-1 min-w-0 flex justify-start md:justify-start">
        <RecipientSelector />
      </div>

      {/* Right side — chips & actions */}
      <div className="flex items-center gap-2 shrink-0">
        <WalletChip className="hidden md:inline-flex" />
        <PointsChip className="hidden lg:inline-flex" />
        <NotificationsBell className="hidden md:inline-flex" />
        <AvatarMenu />
      </div>
    </header>
  );
}
